import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import MigrationCrunchFooter from '../components/MigrationCrunchFooter';
import {
  Database, ArrowRight, UploadSimple, Sparkle, CreditCard,
  EnvelopeSimple, Key, MagicWand, FileArrowDown, ChatCircleDots
} from '@phosphor-icons/react';

/**
 * /migrate/walkthrough — visual, scroll-through guide of the entire
 * MigrationCrunch flow (10 screenshots, alternating left/right).
 * Goal: lower buyer friction by showing exactly what happens after
 * "Upload your file" and after "Pay $599". Linked from the
 * MigrationCrunch nav, hero CTA, and footer.
 */

const STEPS = [
  {
    n: 1,
    icon: Database,
    img: '/brand/walkthrough/step-01-homepage-hero.jpg',
    alt: 'MigrationCrunch homepage hero',
    title: 'Land on the homepage',
    body: (
      <>
        Pick where you&apos;re coming from in the dropdown — ACT!, Goldmine,
        Outlook, Excel, Salesforce, whatever. The headline and the rest of the
        page reshape themselves to your situation, so the copy makes sense for{' '}
        <strong className="text-white">your</strong> CRM mess, not a generic
        one. No signup. No credit card. You&apos;re just looking around.
      </>
    ),
  },
  {
    n: 2,
    icon: UploadSimple,
    img: '/brand/walkthrough/step-02-preview-tool.jpg',
    alt: 'Free preview tool — upload your file',
    title: 'Drop your file into the free preview',
    body: (
      <>
        Hit the big amber button and drop in your export —{' '}
        <code className="bg-white/10 px-1 rounded text-[12px]">.csv</code>,{' '}
        <code className="bg-white/10 px-1 rounded text-[12px]">.xlsx</code>,{' '}
        <code className="bg-white/10 px-1 rounded text-[12px]">.dbf</code>,
        even an old ACT! database. The first 50 contacts run free — with our
        patent-pending AI note extraction turned on — so you see exactly what
        makes us different before paying a dime.
      </>
    ),
  },
  {
    n: 3,
    icon: Sparkle,
    img: '/brand/walkthrough/step-03-extraction-results.jpg',
    alt: 'AI extraction results — notes recovered',
    title: 'Watch the AI re-stitch your notes',
    body: (
      <>
        This is the magic part. Our AI reads every free-text history entry —
        meeting notes, hand-typed phone logs, &quot;left voicemail Tuesday&quot;
        — and figures out which contact each one belongs to. You&apos;ll see a
        live count of contacts found, notes recovered, and a preview of a few
        records so you know it&apos;s working before you pay.
      </>
    ),
  },
  {
    n: 4,
    icon: CreditCard,
    img: '/brand/walkthrough/step-04-pricing.jpg',
    alt: 'Pricing tiers',
    title: 'Pick a tier that fits your file size',
    body: (
      <>
        Five tiers, one-time price, no subscription. Just pick the one that
        covers your contact count — Starter, Growth, Pro, Enterprise, or
        Unlimited. If you&apos;re on the fence, hit{' '}
        <span className="text-amber-300">Fit Wizard</span> and answer three
        questions; we&apos;ll tell you exactly which tier to grab.
      </>
    ),
  },
  {
    n: 5,
    icon: CreditCard,
    img: '/brand/walkthrough/step-05-stripe-checkout.jpg',
    alt: 'Stripe checkout',
    title: 'Pay through Stripe — same as everywhere else',
    body: (
      <>
        Click your tier and you&apos;re bounced over to a hosted Stripe
        checkout. Card, Apple Pay, Google Pay — whatever&apos;s normal. No
        sketchy form on our side, no card numbers ever touching our servers.
        30-day money-back guarantee if the AI note recovery doesn&apos;t work
        on your data.
      </>
    ),
  },
  {
    n: '5a',
    icon: EnvelopeSimple,
    img: '/brand/walkthrough/step-05a-email-gate.jpg',
    alt: 'Email confirmation gate before Stripe',
    title: 'Confirm your email — twice',
    body: (
      <>
        Right before Stripe loads, we ask for your email twice. Yes, it&apos;s
        annoying. It exists because one typo (
        <code className="bg-white/10 px-1 rounded text-[12px]">
          gnial.com
        </code>{' '}
        instead of{' '}
        <code className="bg-white/10 px-1 rounded text-[12px]">gmail.com</code>
        ) means your license key gets emailed to nobody. We&apos;d rather
        catch it now than untangle it later.
      </>
    ),
  },
  {
    n: 6,
    icon: EnvelopeSimple,
    img: '/brand/walkthrough/step-06-license-email.jpg',
    alt: 'License key email delivery',
    title: 'License key lands in your inbox',
    body: (
      <>
        Within about 30 seconds of paying, an email shows up from{' '}
        <code className="bg-white/10 px-1 rounded text-[12px]">
          chip@migrationcrunch.com
        </code>{' '}
        with your license key and a one-click button back into the tool. Save
        the email — it&apos;s your receipt and your re-login link rolled
        together.
      </>
    ),
  },
  {
    n: 7,
    icon: Key,
    img: '/brand/walkthrough/step-07-license-active.jpg',
    alt: 'License active — back inside the tool',
    title: "You're back inside, unlocked",
    body: (
      <>
        Click the link in the email and you&apos;re back where you left off —
        same file, same preview — except now the 50-contact cap is gone and
        the full run is queued. You&apos;ll see a green &quot;License
        active&quot; badge in the corner. If you ever lose the email, just
        paste the license key into the box manually.
      </>
    ),
  },
  {
    n: 8,
    icon: MagicWand,
    img: '/brand/walkthrough/step-08-mapping.jpg',
    alt: 'AI-mapped fields',
    title: 'Confirm the field mapping',
    body: (
      <>
        The AI has already done the hard part — figured out that{' '}
        <code className="bg-white/10 px-1 rounded text-[12px]">CONTACT</code>{' '}
        is &quot;Full Name&quot;,{' '}
        <code className="bg-white/10 px-1 rounded text-[12px]">PHONE1</code>{' '}
        is &quot;Work Phone&quot;, etc. (Anyone who&apos;s done an ACT! export
        knows this is the painful part.) Scan the suggestions, override
        anything that looks off, and hit{' '}
        <span className="text-amber-300">Continue</span>.
      </>
    ),
  },
  {
    n: 9,
    icon: FileArrowDown,
    img: '/brand/walkthrough/step-09-preflight.jpg',
    alt: 'Pre-flight and download CSV',
    title: 'Pre-flight & download your clean CSV',
    body: (
      <>
        Almost done. The Pre-flight screen shows you exactly what&apos;s in
        your file — total contacts, total notes recovered, and a sanity check
        on the mapping. Spot-check a few records, then hit{' '}
        <span className="text-amber-300">Download CSV</span>.
        <br />
        <br />
        Your file is now perfectly formatted for the CRM you&apos;re moving to
        — Pipedrive, HubSpot, Zoho, Salesforce, Monday, you name it. Just
        import the CSV in your new CRM and you&apos;re live.
        <br />
        <br />
        Stuck or something looks off? Reply to your license email — Chip
        personally answers within a few hours.
      </>
    ),
  },
];

export default function WalkthroughPage() {
  useEffect(() => {
    document.title = 'How MigrationCrunch works — the full walkthrough';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      className="min-h-screen bg-[#0a0e27] text-white"
      data-testid="walkthrough-page"
    >
      {/* Top bar — mirrors MigrationCrunchPage so layout feels native */}
      <header className="border-b border-white/10 px-6 py-4 sticky top-0 bg-[#0F0F1A]/95 backdrop-blur-sm z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <Link
            to="/migrate"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            data-testid="walkthrough-nav-logo"
          >
            <Database size={28} weight="fill" className="text-[#F39C12]" />
            <span className="text-xl font-black">
              Migration<span className="text-[#F39C12]">Crunch</span>
            </span>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link
              to="/migrate"
              className="hidden md:inline text-white/70 hover:text-amber-300 transition-colors"
              data-testid="walkthrough-nav-home"
            >
              Home
            </Link>
            <Link
              to="/migrate#pricing"
              className="hidden md:inline text-white/70 hover:text-amber-300 transition-colors"
              data-testid="walkthrough-nav-pricing"
            >
              Pricing
            </Link>
            <Link
              to="/migrate"
              className="bg-[#F39C12] hover:bg-[#E08C00] text-black font-bold px-4 py-2 rounded transition-colors"
              data-testid="walkthrough-nav-cta"
            >
              Start free →
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 py-16 text-center max-w-3xl mx-auto">
        <p
          className="text-[#F39C12] text-sm font-bold uppercase tracking-widest mb-3"
          data-testid="walkthrough-tagline"
        >
          A full tour, no salesy nonsense
        </p>
        <h1
          className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] mb-6"
          data-testid="walkthrough-headline"
        >
          Here&apos;s exactly what happens
          <br />
          <span className="text-[#F39C12]">from upload to clean CSV.</span>
        </h1>
        <p className="text-lg text-white/70 leading-relaxed">
          Nine screens, in order. About a 2-minute read — and roughly the same
          time it takes to actually run the tool end-to-end. If anything
          here&apos;s unclear,{' '}
          <a
            href="mailto:chip@migrationcrunch.com"
            className="text-amber-300 underline hover:text-amber-200"
            data-testid="walkthrough-email-chip"
          >
            email Chip
          </a>
          .
        </p>

        {/* Sticky-ish jump nav */}
        <div className="mt-10 flex flex-wrap justify-center gap-2 max-w-2xl mx-auto">
          {STEPS.map((s) => (
            <a
              key={s.n}
              href={`#step-${s.n}`}
              className="text-xs font-bold bg-white/5 hover:bg-white/10 border border-white/15 hover:border-amber-300/50 text-white/70 hover:text-amber-300 px-3 py-1.5 rounded transition-colors"
              data-testid={`walkthrough-jump-${s.n}`}
            >
              Step {s.n}
            </a>
          ))}
        </div>
      </section>

      {/* Steps — alternating left/right scroll */}
      <section className="px-6 pb-24">
        <div className="max-w-6xl mx-auto space-y-24">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            const flip = idx % 2 === 1;
            return (
              <article
                key={s.n}
                id={`step-${s.n}`}
                className="scroll-mt-24 grid md:grid-cols-2 gap-10 lg:gap-16 items-center"
                data-testid={`walkthrough-step-${s.n}`}
              >
                {/* Screenshot */}
                <div
                  className={`${
                    flip ? 'md:order-2' : ''
                  } rounded-xl overflow-hidden border border-white/10 bg-white/5 shadow-2xl shadow-black/40`}
                >
                  <img
                    src={s.img}
                    alt={s.alt}
                    loading="lazy"
                    className="w-full h-auto block"
                  />
                </div>
                {/* Copy */}
                <div className={`${flip ? 'md:order-1' : ''}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span
                      className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#F39C12] text-black font-black text-base"
                      data-testid={`walkthrough-step-num-${s.n}`}
                    >
                      {s.n}
                    </span>
                    <Icon
                      size={22}
                      weight="bold"
                      className="text-amber-300/80"
                    />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-4 text-white">
                    {s.title}
                  </h2>
                  <p className="text-base text-white/75 leading-relaxed">
                    {s.body}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Closing CTA */}
      <section
        className="px-6 py-16 border-t border-white/10 bg-white/5"
        data-testid="walkthrough-cta-section"
      >
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-4">
            That&apos;s the whole thing.
          </h2>
          <p className="text-lg text-white/70 mb-8 leading-relaxed">
            No phone calls, no implementation specialist, no $5,000 invoice.
            Drop your file in, see what we recover, and pay only if it
            actually solves your problem.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/migrate"
              className="bg-[#F39C12] hover:bg-[#E08C00] text-black font-bold px-8 py-4 rounded text-base inline-flex items-center gap-2 transition-colors"
              data-testid="walkthrough-cta-upload"
            >
              <UploadSimple size={20} weight="bold" />
              Try it free → 50 contacts + notes
            </Link>
            <a
              href="mailto:chip@migrationcrunch.com"
              className="text-sm text-white/70 hover:text-amber-300 inline-flex items-center gap-2 px-4 py-2 transition-colors"
              data-testid="walkthrough-cta-email"
            >
              <ChatCircleDots size={18} weight="bold" />
              Or ask Chip a question first
            </a>
          </div>
          <p className="text-xs text-white/40 mt-3">
            Curious what you&apos;ll save vs an agency?{' '}
            <Link
              to="/migrate#calculator"
              className="text-amber-300 hover:text-amber-200 underline font-bold"
              data-testid="walkthrough-cta-calculator"
            >
              Try the savings calculator →
            </Link>
          </p>
          <p className="text-xs text-white/40 mt-4">
            <ArrowRight size={11} weight="bold" className="inline mr-1" />
            No signup. No credit card. 30-day money-back guarantee.
          </p>
          <p className="text-sm text-white/60 mt-8 pt-6 border-t border-white/10 max-w-xl mx-auto">
            Once your data is clean, you&apos;ll need somewhere to put it.{' '}
            <Link
              to="/walkthrough"
              className="text-amber-300 hover:text-amber-200 underline font-semibold"
              data-testid="mc-walkthrough-cross-link"
            >
              See SalesCrunchCRM in action →
            </Link>
          </p>
        </div>
      </section>

      <MigrationCrunchFooter />
    </div>
  );
}
